import type {
  RoutingLoadEffectiveLayerEntry,
  RoutingLoadEffectiveLayerScope,
  RoutingLoadEffectiveSettings,
  RoutingLoadScopeKind,
  RoutingLoadScopeSettingsRecord,
  RoutingLoadScopeSettingsValues,
  UpdateRoutingLoadScopeSettingsRequest,
} from '@nadoutong/contracts';
import { TIMEOUT_ENABLE_PREFILL_MS } from './loadSettingsFields.ts';
import type { LayerKillSwitchField } from './loadSettingsFields.ts';

export const SCOPE_BOOLEAN_FIELDS = [
  'l2_enabled',
  'l3_enabled',
  'l4_enabled',
  'l3_inferred_caps_enabled',
  'l2_observed_caps_enabled',
  'affinity_conversation_enabled',
  'session_rehome_enabled',
] as const;

export type ScopeBooleanField = (typeof SCOPE_BOOLEAN_FIELDS)[number];

export const SCOPE_TIMEOUT_FIELD = 'attempt_first_byte_timeout_ms' as const;

export type ScopeTimeoutField = typeof SCOPE_TIMEOUT_FIELD;

export const SCOPE_NUMERIC_FIELDS = [
  'shed_warm_threshold',
  'session_rehome_after_429',
  'session_rehome_ttl_seconds',
  'session_bucket_rpm',
  'sticky_escape_error_rate',
  'pool_key_rpm_ceiling',
] as const;

export type ScopeNumericField = (typeof SCOPE_NUMERIC_FIELDS)[number];

export type ScopeOverlayField = ScopeBooleanField | ScopeTimeoutField | ScopeNumericField;

export type ScopeTriState = 'follow' | 'on' | 'off';

export type ScopeFieldSource = 'sku' | 'namespace' | 'global';

export const EMPTY_SCOPE_VALUES: RoutingLoadScopeSettingsValues = {
  l2_enabled: null,
  l3_enabled: null,
  l4_enabled: null,
  l3_inferred_caps_enabled: null,
  l2_observed_caps_enabled: null,
  affinity_conversation_enabled: null,
  attempt_first_byte_timeout_ms: null,
  session_rehome_enabled: null,
  shed_warm_threshold: null,
  session_rehome_after_429: null,
  session_rehome_ttl_seconds: null,
  session_bucket_rpm: null,
  sticky_escape_error_rate: null,
  pool_key_rpm_ceiling: null,
};

/** One-click “turn load-aware on for this lane”. Timeout uses the same prefill as global enable. */
export const LANE_ENABLE_LOAD_PATCH: UpdateRoutingLoadScopeSettingsRequest = {
  l2_enabled: true,
  l3_enabled: true,
  l3_inferred_caps_enabled: true,
  l4_enabled: true,
  affinity_conversation_enabled: true,
  attempt_first_byte_timeout_ms: TIMEOUT_ENABLE_PREFILL_MS,
};

export const LANE_ENABLE_FIELDS = [
  'l2_enabled',
  'l3_enabled',
  'l3_inferred_caps_enabled',
  'l4_enabled',
  'affinity_conversation_enabled',
  'attempt_first_byte_timeout_ms',
] as const;

export function booleanTriState(stored: boolean | null | undefined): ScopeTriState {
  if (stored === true) return 'on';
  if (stored === false) return 'off';
  return 'follow';
}

export function timeoutTriState(stored: number | null | undefined): Exclude<ScopeTriState, 'off'> {
  return stored === null || stored === undefined ? 'follow' : 'on';
}

export function booleanTriStatePatch(
  field: ScopeBooleanField,
  next: ScopeTriState,
): UpdateRoutingLoadScopeSettingsRequest {
  if (next === 'follow') return { [field]: null };
  if (next === 'on') return { [field]: true };
  return { [field]: false };
}

export function timeoutTriStatePatch(
  next: Exclude<ScopeTriState, 'off'>,
): UpdateRoutingLoadScopeSettingsRequest {
  return {
    attempt_first_byte_timeout_ms: next === 'follow' ? null : TIMEOUT_ENABLE_PREFILL_MS,
  };
}

export function numericOverlayPatch(
  field: ScopeNumericField,
  value: number | null,
): UpdateRoutingLoadScopeSettingsRequest {
  return { [field]: value };
}

export function scopeRowOf(
  items: readonly RoutingLoadScopeSettingsRecord[] | undefined,
  kind: RoutingLoadScopeKind,
  id: string,
): RoutingLoadScopeSettingsRecord | null {
  if (items === undefined) return null;
  return items.find((item) => item.scope_kind === kind && item.scope_id === id) ?? null;
}

export function killSwitchForBooleanField(field: ScopeBooleanField): LayerKillSwitchField | null {
  if (field === 'l2_enabled') return 'l2_env_kill_switch';
  if (field === 'l3_enabled') return 'l3_env_kill_switch';
  if (field === 'l4_enabled') return 'l4_env_kill_switch';
  if (field === 'session_rehome_enabled') return 'session_rehome_env_kill_switch';
  return null;
}

export interface ResolvedScopeBoolean {
  readonly value: boolean;
  readonly source: ScopeFieldSource;
}

export interface ResolvedScopeTimeout {
  readonly value: number | null;
  readonly source: ScopeFieldSource;
}

export function resolveScopeBoolean(params: {
  readonly sku: boolean | null | undefined;
  readonly namespace: boolean | null | undefined;
  readonly globalEffective: boolean;
  readonly envKilled: boolean;
}): ResolvedScopeBoolean {
  let source: ScopeFieldSource = 'global';
  let value = params.globalEffective;
  if (params.namespace !== null && params.namespace !== undefined) {
    source = 'namespace';
    value = params.namespace;
  }
  if (params.sku !== null && params.sku !== undefined) {
    source = 'sku';
    value = params.sku;
  }
  return { value: params.envKilled ? false : value, source };
}

export function resolveScopeTimeout(params: {
  readonly sku: number | null | undefined;
  readonly namespace: number | null | undefined;
  readonly globalEffective: number | null;
}): ResolvedScopeTimeout {
  if (params.sku !== null && params.sku !== undefined) {
    return { value: params.sku, source: 'sku' };
  }
  if (params.namespace !== null && params.namespace !== undefined) {
    return { value: params.namespace, source: 'namespace' };
  }
  return { value: params.globalEffective, source: 'global' };
}

export function resolveScopeBooleans(params: {
  readonly sku: RoutingLoadScopeSettingsValues | null;
  readonly namespace: RoutingLoadScopeSettingsValues | null;
  readonly globalEffective: RoutingLoadEffectiveSettings;
  readonly kill: {
    readonly l2: boolean;
    readonly l3: boolean;
    readonly l4: boolean;
    readonly sessionRehome: boolean;
  };
}): Record<ScopeBooleanField, ResolvedScopeBoolean> {
  const overlay = (field: ScopeBooleanField, envKilled: boolean): ResolvedScopeBoolean =>
    resolveScopeBoolean({
      sku: params.sku?.[field],
      namespace: params.namespace?.[field],
      globalEffective: params.globalEffective[field],
      envKilled,
    });
  return {
    l2_enabled: overlay('l2_enabled', params.kill.l2),
    l3_enabled: overlay('l3_enabled', params.kill.l3),
    l4_enabled: overlay('l4_enabled', params.kill.l4),
    l3_inferred_caps_enabled: overlay('l3_inferred_caps_enabled', false),
    l2_observed_caps_enabled: overlay('l2_observed_caps_enabled', false),
    affinity_conversation_enabled: overlay('affinity_conversation_enabled', false),
    session_rehome_enabled: overlay('session_rehome_enabled', params.kill.sessionRehome),
  };
}

/** `unknown` is a missing sku/namespace on an old L2 hash, not “layer off”. */
export function isUnknownLayerScope(scope: RoutingLoadEffectiveLayerScope): boolean {
  return scope === 'unknown';
}

export function layerEntryKey(entry: RoutingLoadEffectiveLayerEntry): string {
  if (entry.sku_id !== null && entry.sku_id.length > 0) return entry.sku_id;
  if (entry.namespace !== null && entry.namespace.length > 0) return `ns:${entry.namespace}`;
  return 'unknown';
}

/** Platform namespace slug: the `sku_name` prefix before `/`. */
export function namespaceFromSkuName(skuName: string | null | undefined): string | null {
  if (skuName === undefined || skuName === null) return null;
  const slash = skuName.indexOf('/');
  if (slash <= 0) return null;
  const prefix = skuName.slice(0, slash).trim();
  return prefix.length > 0 ? prefix : null;
}
